import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'

// Per-user FieldHub calendar label toggles. Persists in
// profiles.calendar_label_preferences (JSONB column from migration
// 20260601000000). Pattern mirrors notification_preferences exactly —
// see src/pages/settings/Settings.tsx:1018-1051.

export type CalendarLabelKey =
  | 'time'
  | 'customer'
  | 'title'
  | 'service_type'
  | 'city_state'
  | 'assignee'
  | 'status_text'

export type CalendarLabelPrefs = Record<CalendarLabelKey, boolean>

// Defaults applied when the user has no saved prefs (or for keys added
// after the user first set theirs — merged on top of saved).
// time + customer approximates the current 2-line label.
export const CALENDAR_LABEL_DEFAULTS: CalendarLabelPrefs = {
  time: true,
  customer: true,
  title: false,
  service_type: false,
  city_state: false,
  assignee: false,
  status_text: false,
}

export function useCalendarLabelPrefs() {
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  const [optimistic, setOptimistic] = useState<Partial<CalendarLabelPrefs> | null>(null)

  const queryKey = ['calendar-label-preferences', profile?.id] as const

  const { data: saved } = useQuery({
    queryKey,
    enabled: !!profile?.id,
    queryFn: async () => {
      if (!profile?.id) return {}
      // .maybeSingle (not .single) — returns {data:null,error:null} for 0
      // rows instead of HTTP 406. Defends against transient PostgREST
      // schema-cache lag immediately after the column was added (a 406
      // storm at startup cascaded into a session refresh failure / logout
      // when this used .single).
      const { data, error } = await supabase
        .from('profiles')
        .select('calendar_label_preferences')
        .eq('id', profile.id)
        .maybeSingle()
      if (error) {
        console.warn('[useCalendarLabelPrefs] load failed:', error.message)
        return {}
      }
      return (data?.calendar_label_preferences ?? {}) as Partial<CalendarLabelPrefs>
    },
  })

  // Resolved prefs = defaults <- saved <- optimistic (optimistic wins so the
  // pill re-renders instantly on toggle; the server upsert syncs in the
  // background).
  const prefs = useMemo<CalendarLabelPrefs>(
    () => ({ ...CALENDAR_LABEL_DEFAULTS, ...(saved ?? {}), ...(optimistic ?? {}) }),
    [saved, optimistic],
  )

  const setPref = (key: CalendarLabelKey, value: boolean) => {
    if (!profile?.id) {
      toast.error('Not signed in')
      return
    }
    // Optimistic apply
    setOptimistic(prev => ({ ...(prev ?? {}), [key]: value }))
    // Persist (full merged blob — no race with concurrent toggles since
    // we always write the resolved state)
    const next = { ...(saved ?? {}), [key]: value } as Partial<CalendarLabelPrefs>
    void supabase
      .from('profiles')
      .update({ calendar_label_preferences: next })
      .eq('id', profile.id)
      .then(({ error }) => {
        if (error) {
          // Revert optimistic + invalidate to re-pull truth
          setOptimistic(prev => {
            if (!prev) return prev
            const { [key]: _, ...rest } = prev
            return Object.keys(rest).length ? rest : null
          })
          queryClient.invalidateQueries({ queryKey })
          toast.error(`Couldn't save preference: ${error.message}`)
        } else {
          // Refresh cache to reflect the truth, then clear the optimistic
          // overlay (the saved data will carry the change going forward).
          queryClient.invalidateQueries({ queryKey })
        }
      })
  }

  // When saved data updates (after a successful invalidate), clear the
  // optimistic overlay so resolved prefs come purely from saved.
  useEffect(() => {
    if (saved && optimistic) {
      // Only clear keys that now match saved
      const stillDifferent: Partial<CalendarLabelPrefs> = {}
      let anyDifferent = false
      for (const key of Object.keys(optimistic) as CalendarLabelKey[]) {
        if (saved[key] !== optimistic[key]) {
          stillDifferent[key] = optimistic[key]
          anyDifferent = true
        }
      }
      setOptimistic(anyDifferent ? stillDifferent : null)
    }
  }, [saved])  // eslint-disable-line react-hooks/exhaustive-deps

  return { prefs, setPref }
}
